import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthProvider';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
  requirePurchase?: boolean;
}

export default function ProtectedRoute({ children, requireAdmin = false, requirePurchase = false }: ProtectedRouteProps) {
  const { user, isAuthenticated, isAdmin, hasPurchased, isLoading } = useAuth();
  const location = useLocation();

  // ponytail: dev-only bypass — on localhost, render protected pages without
  // login/purchase so the reader is viewable in development. No effect in prod.
  if (import.meta.env.DEV) {
    return <>{children}</>;
  }

  // Show loading state
  if (isLoading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--off-white)'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '3px solid var(--light-gray)',
            borderTopColor: 'var(--primary)',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 1rem'
          }} />
          <p style={{ color: 'var(--text-light)', margin: 0 }}>Loading...</p>
        </div>
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // Not authenticated OR no user object — redirect to login
  if (!isAuthenticated || !user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Requires admin but user is not admin
  if (requireAdmin && !isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  // Requires purchase but user hasn't purchased (admins bypass)
  if (requirePurchase && !hasPurchased && !isAdmin) {
    return <Navigate to="/join" replace />;
  }

  return <>{children}</>;
}
